/**
 * Reine Logik für den Top-Bar-Server-Switcher. Härtet die Anzeige gegen einen
 * aktiven Server, der (noch) nicht in der Liste steht oder keine Live-Daten hat:
 * der gerade angelegte/gelöschte Server bzw. ein fehlschlagender Connect darf den
 * Render von Switcher + „+ Neuer Server" NIE mitreißen. Statt eines Throws gibt
 * es immer einen konsistenten Zustand (Fallback-Option für die aktive ID).
 */

export interface SwitcherOption {
  id: string;
  label: string;
  /** True, wenn die aktive ID (noch) nicht in der Serverliste steht. */
  missing?: boolean;
}

export interface SwitcherState {
  options: SwitcherOption[];
  /** Aktuell ausgewählter Wert (leer = kein Server). */
  value: string;
  /** True, wenn gar keine Server existieren. */
  empty: boolean;
}

export interface SwitcherServer {
  id: string;
  name?: string | null;
}

/**
 * Baut den Switcher-Zustand defensiv. Tolerant gegen `null`/leere Eingaben und
 * gegen eine aktive ID, die nicht in `servers` vorkommt (dann als markierte
 * Fallback-Option angehängt, damit das `<select>` konsistent bleibt).
 */
export function serverSwitcherState(
  servers: readonly SwitcherServer[] | null | undefined,
  activeId: string | null | undefined,
): SwitcherState {
  const list = Array.isArray(servers) ? servers : [];
  const options: SwitcherOption[] = list
    .filter((s) => s && typeof s.id === "string" && s.id !== "")
    .map((s) => ({ id: s.id, label: (s.name ?? "").toString().trim() || s.id }));

  const value = activeId ?? "";
  if (value !== "" && !options.some((o) => o.id === value)) {
    // Aktive ID (noch) nicht in der Liste: als Fallback anzeigen, nicht verschlucken.
    options.push({ id: value, label: value, missing: true });
  }

  return { options, value, empty: options.length === 0 };
}
